import * as React from "react";
import { Navigate } from "react-router";
import { useMe } from "@/api/me";
import { useAuthStore } from "@/store/authStore";

interface Props {
  children: React.ReactNode;
}

/**
 * Wraps an O&M / Admin route so a SiteOwner direct-URL hit redirects
 * to `/my-plants` instead of seeing an operational surface.
 *
 * `RoleGate` operates at the Cognito group level (SuperAdmin /
 * ClientAdmin / User); SiteOwner shares the `User` group with O&M
 * and only diverges at the `primary_persona` level computed
 * server-side in `/me`.  This wrapper closes that gap for routes
 * that should never render for SiteOwners (05 alarms, 06 tickets,
 * 04 inbox, etc.).
 *
 * Sidebar already excludes these routes for SiteOwner — this is the
 * defence-in-depth for direct-URL hits.
 */
export function SiteOwnerRedirect({ children }: Props): React.ReactElement {
  const status = useAuthStore((s) => s.status);
  const { data, isLoading } = useMe({ enabled: status === "authenticated" });

  if (isLoading || !data) {
    return (
      <div
        role="status"
        aria-busy="true"
        className="flex h-full w-full items-center justify-center"
      >
        <div className="h-6 w-6 rounded-full border-2 border-[color:var(--gray-300)] border-t-[color:var(--brand-primary)] animate-spin" />
      </div>
    );
  }
  if (data.primary_persona === "SiteOwner") {
    return <Navigate to="/my-plants" replace />;
  }
  return <>{children}</>;
}
